import React, { useMemo, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { components, palette, typography } from '../../utils/designTokens';
import {
  getTableCellColor,
  getTableHeaderStyle,
  getTableRowStyle,
  TableCell,
  TableHeaderCell,
  TableHeaderRow,
  TableSortHeader,
  tablePanelTitleClassName,
} from '../TablePrimitives';
import { formatCompactCurrency, formatInteger, formatPercent } from './costFormatters';

export const COLUMN_DEFS = [
  { key: 'label', label: 'Name', align: 'left', width: '210px', tone: 'entity', render: (row) => row.label || row.key || '-' },
  { key: 'total_cost_usd', label: 'Spend', align: 'right', width: '120px', tone: 'metric', render: (row) => formatCompactCurrency(row.total_cost_usd) },
  { key: 'trace_count', label: 'Traces', align: 'right', width: '90px', tone: 'metric', optional: true, render: (row) => formatInteger(row.trace_count) },
  { key: 'avg_cost_per_trace_usd', label: 'Avg / Trace', align: 'right', width: '120px', tone: 'metric', optional: true, render: (row) => formatCompactCurrency(row.avg_cost_per_trace_usd) },
  { key: 'prompt_tokens', label: 'Input Tokens', align: 'right', width: '130px', tone: 'metric', render: (row) => formatInteger(row.prompt_tokens) },
  { key: 'completion_tokens', label: 'Output Tokens', align: 'right', width: '130px', tone: 'metric', render: (row) => formatInteger(row.completion_tokens) },
  { key: 'cached_tokens', label: 'Cached Tokens', align: 'right', width: '130px', tone: 'metric', render: (row) => formatInteger(row.cached_tokens) },
  { key: 'total_tokens', label: 'Total Tokens', align: 'right', width: '130px', tone: 'metric', render: (row) => formatInteger(row.total_tokens) },
  { key: 'share_of_spend_pct', label: 'Share', align: 'right', width: '90px', tone: 'supporting', render: (row) => formatPercent(row.share_of_spend_pct, 2) },
  { key: 'delta_cost_pct', label: 'Delta', align: 'right', width: '90px', tone: 'supporting', optional: true, render: (row) => row.delta_cost_pct == null ? '—' : formatPercent(row.delta_cost_pct, 2) },
];

const SORT_TOOLTIP_TEXT = 'Sorts loaded rows only';

const compareValues = (left, right, direction) => {
  if (left == null && right == null) return 0;
  if (left == null) return 1;
  if (right == null) return -1;

  if (typeof left === 'string' || typeof right === 'string') {
    const result = String(left).localeCompare(String(right));
    return direction === 'asc' ? result : -result;
  }

  const result = left - right;
  return direction === 'asc' ? result : -result;
};

export const sortBreakdownRows = (rows, sortKey, sortDirection) => (
  [...rows].sort((left, right) => {
    const comparison = compareValues(left?.[sortKey], right?.[sortKey], sortDirection);
    if (comparison !== 0) return comparison;
    return compareValues(left?.label || left?.key, right?.label || right?.key, 'asc');
  })
);

export const getVisibleColumns = (rows) => (
  COLUMN_DEFS.filter(column => !column.optional || rows.some(row => row?.[column.key] != null))
);

const InfoTooltipButton = ({ text }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative inline-flex items-center">
      <button
        type="button"
        aria-label={text}
        title={text}
        className="inline-flex items-center"
        style={{ color: palette.text.supporting }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((current) => !current);
        }}
      >
        <AlertCircle size={12} />
      </button>
      {open ? (
        <div
          className={`${typography.size.label} absolute right-0 top-full z-10 mt-2 rounded-md px-2 py-1.5`}
          style={{
            minWidth: '220px',
            maxWidth: '260px',
            color: palette.text.primary,
            backgroundColor: palette.neutrals.panelPrimary,
            border: `1px solid ${components.card.borderColor}`,
            boxShadow: palette.quality.shadowCard,
            textTransform: 'none',
            letterSpacing: 'normal',
            whiteSpace: 'normal',
          }}
        >
          {text}
        </div>
      ) : null}
    </div>
  );
};

export default function CostBreakdownSection({
  title,
  dimensionKey,
  rows,
  loading,
  error,
  appId,
  environment,
  timeWindow,
  emptyTitle,
  emptyDetail,
  collapsedRowCount,
  onRowClick,
}) {
  if (!title) throw new Error('CostBreakdownSection requires title');
  if (!dimensionKey) throw new Error('CostBreakdownSection requires dimensionKey');
  if (!Array.isArray(rows)) throw new Error(`CostBreakdownSection rows must be an array for ${dimensionKey}`);

  const [sortKey, setSortKey] = useState('total_cost_usd');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expanded, setExpanded] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const sortedRows = useMemo(() => {
    return sortBreakdownRows(rows, sortKey, sortDirection);
  }, [rows, sortDirection, sortKey]);

  const visibleColumns = useMemo(() => getVisibleColumns(rows), [rows]);
  const tableMinWidth = visibleColumns.reduce((sum, column) => sum + parseInt(column.width, 10), 0);

  const handleSort = (nextKey) => {
    if (nextKey === sortKey) {
      setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
      return;
    }

    setSortKey(nextKey);
    setSortDirection(nextKey === 'label' ? 'asc' : 'desc');
  };

  const visibleRows = collapsedRowCount && !expanded
    ? sortedRows.slice(0, collapsedRowCount)
    : sortedRows;

  return (
    <section
      className={`${components.card.bg} ${components.card.rounded} ${components.card.padding}`}
      style={{ border: `1px solid ${components.card.borderColor}` }}
      data-app-id={appId || ''}
      data-environment={environment || ''}
      data-time-window={timeWindow || ''}
    >
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className={tablePanelTitleClassName} style={{ color: palette.text.supporting }}>
            {title}
          </div>
        </div>
        <div className={typography.size.label} style={{ color: palette.text.supporting }}>
          {formatInteger(rows.length)} row{rows.length === 1 ? '' : 's'}
        </div>
      </div>

      {loading ? (
        <div className={`${components.table.empty} mt-4`}>Loading {dimensionKey} cost…</div>
      ) : null}

      {!loading && error ? (
        <div className={`${components.table.empty} mt-4`}>
          <div className="text-danger">{error}</div>
        </div>
      ) : null}

      {!loading && !error && rows.length === 0 ? (
        <div className={`${components.table.empty} mt-4`}>
          <div>{emptyTitle || `No ${dimensionKey} cost data for this window.`}</div>
          {emptyDetail ? (
            <div className={`${typography.size.sm} mt-2`} style={{ color: palette.text.supporting }}>
              {emptyDetail}
            </div>
          ) : null}
        </div>
      ) : null}

      {!loading && !error && rows.length > 0 ? (
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full border-separate border-spacing-0" style={{ minWidth: `${tableMinWidth}px`, tableLayout: 'fixed' }}>
            <thead>
              <TableHeaderRow as="tr" style={getTableHeaderStyle()}>
                {visibleColumns.map((column) => (
                  <TableHeaderCell
                    as="th"
                    key={column.key}
                    compact
                    align={column.align}
                    style={{
                      borderBottom: `1px solid ${components.card.borderColor}`,
                      width: column.width,
                    }}
                  >
                    <TableSortHeader
                      active={sortKey === column.key}
                      direction={sortDirection}
                      label={column.label}
                      onClick={() => handleSort(column.key)}
                      align={column.align}
                      activeAccessory={<InfoTooltipButton text={SORT_TOOLTIP_TEXT} />}
                    />
                  </TableHeaderCell>
                ))}
              </TableHeaderRow>
            </thead>
            <tbody>
              {visibleRows.map((row, index) => {
                const isHovered = onRowClick && hoveredIndex === index;
                return (
                <tr
                  key={row.key || `${dimensionKey}-${index}`}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  onMouseEnter={onRowClick ? () => setHoveredIndex(index) : undefined}
                  onMouseLeave={onRowClick ? () => setHoveredIndex(null) : undefined}
                  style={getTableRowStyle(index, { isHovered, interactive: Boolean(onRowClick) })}
                >
                  {visibleColumns.map((column) => (
                    <TableCell
                      as="td"
                      key={column.key}
                      align={column.align}
                      style={{
                        color: getTableCellColor(column.tone),
                        borderBottom: `1px solid ${components.card.borderColor}`,
                        overflow: 'hidden',
                        textOverflow: column.key === 'label' ? 'ellipsis' : 'clip',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {column.render(row)}
                    </TableCell>
                  ))}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {!loading && !error && collapsedRowCount && sortedRows.length > collapsedRowCount ? (
        <button
          type="button"
          className="mt-3 py-1.5 rounded border text-xs font-medium transition-colors hover:opacity-90"
          style={{
            borderColor: palette.neutrals.borderSubtle,
            color: palette.accents.primary.base,
            backgroundColor: palette.neutrals.surfaceSubtle,
          }}
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? `Show Top ${collapsedRowCount}` : `Show All (${sortedRows.length})`}
        </button>
      ) : null}
    </section>
  );
}
